import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { ExternalLink, Calendar, Award, Star, User, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import mockData from '../mock';
import CorporateHeader from './CorporateHeader';
import Footer from './Footer';

const PortfolioPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black">
      <CorporateHeader />
      
      {/* Hero */}
      <section className="pt-28 pb-16 bg-gradient-to-b from-black via-gray-900 to-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-red-600/20 text-red-500 border-red-600/30 mb-4">
            <Award className="w-3 h-3 mr-1" />
            18+ Years of Excellence
          </Badge>
          <h1 className="text-4xl lg:text-6xl font-black text-white mb-4">Portfolio</h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Celebrity interviews, award-winning publications, and media that moves culture.
          </p>
        </div>
      </section>

      {/* The Movement Magazine Spotlight */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="bg-gradient-to-br from-gray-900 to-black border border-gray-800 rounded-3xl overflow-hidden">
              <div className="grid lg:grid-cols-2">
                {/* Content */}
                <div className="p-8 lg:p-12">
                  <Badge className="bg-red-600 text-white mb-6">
                    <Award className="w-4 h-4 mr-2" />
                    Flagship Publication
                  </Badge>
                  <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                    {mockData.theMovementMagazine?.title || 'The Movement Magazine'}
                  </h2>
                  <p className="text-xl text-red-500 font-semibold mb-6 italic">
                    "{mockData.theMovementMagazine?.tagline || 'Where Culture Meets Purpose'}"
                  </p>
                  
                  <p className="text-gray-400 leading-relaxed mb-6">
                    {mockData.theMovementMagazine?.description || 'A premier publication spotlighting A-list celebrities, entrepreneurs, and change-makers who shape culture and inspire millions.'}
                  </p>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-6 mb-8">
                    <div className="text-center p-4 bg-black/50 rounded-xl">
                      <div className="text-2xl font-bold text-red-500 mb-1">18+</div>
                      <div className="text-xs text-gray-500">Years Publishing</div>
                    </div>
                    <div className="text-center p-4 bg-black/50 rounded-xl">
                      <div className="text-2xl font-bold text-red-500 mb-1">100+</div>
                      <div className="text-xs text-gray-500">Celebrity Features</div>
                    </div>
                    <div className="text-center p-4 bg-black/50 rounded-xl">
                      <div className="text-2xl font-bold text-red-500 mb-1">A-List</div>
                      <div className="text-xs text-gray-500">Exclusive Access</div>
                    </div>
                  </div>

                  <p className="text-sm text-gray-500 mb-6">
                    — {mockData.theMovementMagazine?.founder || 'Audrey Egypt Young, Founder'}
                  </p>
                </div>

                {/* Visual */}
                <div className="bg-gradient-to-br from-red-600 to-red-800 p-8 lg:p-12 flex items-center justify-center">
                  <div className="text-center text-white">
                    <User className="w-24 h-24 mx-auto mb-6 opacity-80" />
                    <h3 className="text-2xl lg:text-3xl font-bold mb-4">Celebrity Access</h3>
                    <p className="text-lg opacity-90 leading-relaxed">
                      Exclusive interviews with Kevin Hart, Bruno Mars, Tyrese Gibson, 
                      Larenz Tate, Frankie Beverly, and many more industry icons.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Magazine Covers Grid */}
      <section className="py-16 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Featured Covers</h2>
            <div className="w-20 h-1 bg-red-600 mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {mockData.magazineWork?.map((work, index) => (
              <Card 
                key={work.id || index} 
                className="group bg-gray-900/50 border-gray-800 hover:border-red-600/50 transition-all duration-300 hover:-translate-y-1 overflow-hidden"
              >
                <div className="relative overflow-hidden aspect-[3/4]">
                  <img 
                    src={work.image}
                    alt={work.title}
                    className="w-full h-full object-contain bg-black transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4">
                    <Badge className="bg-red-600 text-white text-xs">
                      <Star className="w-3 h-3 mr-1 fill-current" />
                      Celebrity Feature
                    </Badge>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                    <div className="p-4 w-full">
                      <Button 
                        className="w-full bg-red-600 hover:bg-red-700 text-white font-medium"
                        onClick={() => window.open(work.issueLink, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Read Full Issue
                      </Button>
                    </div>
                  </div>
                </div>
                <CardContent className="p-4">
                  <div className="flex items-center text-xs text-gray-500 mb-2">
                    <Calendar className="w-3 h-3 mr-1" />
                    {work.publication}
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2 group-hover:text-red-500 transition-colors line-clamp-2">
                    {work.title}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed line-clamp-2">
                    {work.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Interested in Being Featured?
            </h2>
            <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
              The Movement Magazine continues to seek compelling stories and influential voices 
              that inspire positive change in our communities.
            </p>
            <Button 
              onClick={() => navigate('/start-project')}
              className="bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-3"
            >
              Submit Your Story
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default PortfolioPage;
